import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { JsonPipe } from '@angular/common';
import { ApiService } from '../../core/services/api.service';
import { KobbleHealthData, ManualCreditPayload } from '../../core/models/api.models';

@Component({
  selector: 'app-kobble',
  standalone: true,
  imports: [ReactiveFormsModule, JsonPipe],
  templateUrl: './kobble.component.html',
  styleUrl: './kobble.component.scss',
})
export class KobbleComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);

  readonly loadingHealth = signal(false);
  readonly savingCredit = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly health = signal<KobbleHealthData | null>(null);

  readonly creditForm = this.fb.nonNullable.group({
    // Local numeric wallets.id OR Kobble wallet UUID
    wallet_ref: ['', [Validators.required]],
    amount: [1000, [Validators.required, Validators.min(0.01)]],
    reference: ['Test topup'],
  });

  ngOnInit(): void {
    this.refreshHealth();
  }

  refreshHealth(): void {
    this.loadingHealth.set(true);
    this.error.set(null);
    this.api.getKobbleHealth().subscribe({
      next: (data) => {
        this.health.set(data);
        this.loadingHealth.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load Kobble health');
        this.loadingHealth.set(false);
      },
    });
  }

  submitCredit(): void {
    if (this.creditForm.invalid || this.savingCredit()) {
      this.creditForm.markAllAsTouched();
      this.error.set('Enter a local wallet ID (number) or Kobble wallet ID, and a valid amount');
      return;
    }

    this.savingCredit.set(true);
    this.error.set(null);
    this.success.set(null);
    const value = this.creditForm.getRawValue();
    const ref = String(value.wallet_ref || '').trim();
    const payload: ManualCreditPayload = {
      amount: Number(value.amount),
      reference: value.reference || undefined,
    };

    if (/^\d+$/.test(ref)) {
      payload.wallet_id = Number(ref);
    } else {
      payload.kobble_wallet_id = ref;
    }

    this.api.addManualCredit(payload).subscribe({
      next: () => {
        this.savingCredit.set(false);
        this.success.set('Manual credit created');
      },
      error: (err: { message?: string }) => {
        this.savingCredit.set(false);
        this.error.set(err?.message || 'Failed to create manual credit');
      },
    });
  }
}
